import React, { useEffect, useState, useCallback} from 'react';
import Select from "react-select";
import { useSelector } from "react-redux";

import CustomGroupsOption from "./CustomGroupsOption";
import Axios from "../../../../../../node_modules/axios/index";
import conxtdOut from "apis/conxtdOut";
import isEqual from "lodash/isEqual";

import { darkModeSelectStyles } from "components/Common/CustomStyles/darkModeSelectStyles";

function GroupsSelect({ 
  enterpriseId, 
  groups, 
  groupsFormData, 
  groupValues, 
  editing, 
  setGroupsFormData, 
  initialFormData, 
  editData,
  editMode,
  editAuthorised,
  setEditAuthorised,
  setGroupValues,
  hidden,
}) {

  const [groupTree, setGroupTree] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkedGroups, setLinkedGroups] = useState([]);
  // const [newGroupValues, setNewGroupValues] = useState([]);
  const [groupsCopy, setGroupsCopy] = useState([]);
  const [noGroupTree, setNoGroupTree] = useState(true);
  const [mappedGroupTree, setMappedGroupTree] = useState([]);

  const { darkMode } = useSelector((state) => state.app);

  useEffect(() => {
    if (Array.isArray(groupValues)) {
      setLinkedGroups(groupValues?.map(group => group?.value ?? group));
      return;
    }
  }, [groupValues])

  useEffect(() => {
    if (groupTree) {
      
      const mapped = groupTree.map((group) => {
        const { id, name, isParent } = group;
        
        const groupsIds = groups ? Object.values(groups).map(group => group?.id ?? group) ?? [] : [];
        const isLinked = groupsIds?.includes(id);

        let formatName = "";
        let remainingText = name;

        for (let i = 0; i < name.length; i++) {
          if (name[i] === "_") {
            formatName = formatName + "\u00A0\u00A0\u00A0";
            remainingText = name.slice(i+1);
          } else {
            break;
          }
        }
        formatName = formatName + remainingText;
        
        return (
          ({ label: formatName, value: id, isParent: isParent, unlink: false, isLinked: isLinked })
        );
      })

      setMappedGroupTree(prev => {
        if (isEqual(prev, mapped)) return prev;
        return mapped;
      });

    }
  }, [linkedGroups, groupTree, editAuthorised, groups])

  const getGroupsOptions = useCallback(() => {
    const source = Axios.CancelToken.source();

    if (enterpriseId) {
      const getGroupsForEnterprise = conxtdOut.get(`/ServiceDesk/getGroupsForEnterprise/${enterpriseId}`, { cancelToken: source.token })
      
      getGroupsForEnterprise.then((res) => {
        if (!enterpriseId) { return };

        setGroupTree(res?.data?.groupTree);

      }).catch((error) => {
        console.log(error)
      })
    }

    return () => {
      source.cancel("Component unmounted: request canceled");
    }
  }, [enterpriseId])

  const checkChildGroups = useCallback(() => {
    if (groupTree) {
      const filteredResults = Array.isArray(groupTree) && groupTree.filter(group => !linkedGroups.includes(group.id));
      if (filteredResults.every(group => group.isParent)) {
        setGroupsFormData([]);
        return;
      }
      setGroupsFormData(mappedGroupTree)
      return;
    }
  }, [groupTree, linkedGroups, editAuthorised, mappedGroupTree])

  useEffect(() => {
    setNoGroupTree(
      (Array.isArray(groupTree) && groupTree.length <= 0) ||
      !groupTree
    )
  }, [groupTree])

  useEffect(() => {
    if (editing) {
      setEditAuthorised(editMode && !noGroupTree)
    }
  }, [editMode, noGroupTree])

  useEffect(() => {
    if (editData instanceof Map && enterpriseId) {
      const copy = editData.get(enterpriseId)?.groups ?? null;
      copy && setGroupsCopy(copy);
    }
  }, [editData, enterpriseId])

  useEffect(() => {
    if (enterpriseId) {
      checkChildGroups();
      getGroupsOptions();
    }
  }, [editing, editAuthorised, enterpriseId, mappedGroupTree])

  const handleSelectedOptions = useCallback((data, selectedOptions) => {
  
      // handler functions ------------------------------------------------>
      if (editing) {
        const initialGroups = enterpriseId && initialFormData instanceof Map ? initialFormData.get(enterpriseId)?.groups
          ?.map(group => group.value ?? null) : null;
    
        const unlinkGroup = (removed) => groupsCopy
        .map(group => group.value === removed.value ? 
          { ...group, unlink: true } 
          : group
        );
        
        const groupIsNew = (item) => !initialGroups.includes(item?.value);
    
        const removeGroup = (group) => {
          const removeNewGroupSelection = (removedValue) =>
            groupValues.filter(g => g.value !== removedValue) ?? groupValues.filter(g => g.value !== removedValue);
    
          return groupIsNew(group)
            ? removeNewGroupSelection(group.value)
            : unlinkGroup(group);
        };
        
        const clearAllGroups = () => {
          const sorted = [];
          if (Array.isArray(groupsCopy) && groupsCopy.length > 0) {
            for (const group of groupsCopy) {
              if (groupIsNew(group)) {
                continue;
              }
              sorted.push({ ...group, unlink: true });
            }
          }
          setGroupValues(sorted);
        }
      // ------------------------------------------------------------------|
  
        switch (data.action) {
          case "remove-value":
            setGroupValues(removeGroup(data.removedValue));
            break;
    
          case "pop-value":
            setGroupValues(removeGroup(data.removedValue));
            break;
    
          case "clear":
            clearAllGroups();
            break;
          
          case "select-option":
              setGroupValues([
                ...groupValues.filter(({ unlink }) => unlink),
                ...selectedOptions,
              ])
            break;
    
          default:
            break;
        }
      }

      if (!editing) {
        switch (data.action) {
          case "remove-value":
            setGroupValues(selectedOptions);
            break;
    
          case "pop-value":
            setGroupValues(selectedOptions);
            break;
    
          case "clear":
            setGroupValues([]);
            break;
          
          case "select-option":
            setGroupValues(selectedOptions)
            break;
    
          default:
            break;
        }
      }
    }, [editData, enterpriseId, groupsCopy, initialFormData, groupValues, editing])

  return (
    <>
      {hidden ?
        <></>
      :
        <Select
        menuPosition="fixed"
        data-intercom-target={"edit-enterprise-group-select-edit"}
        name="enterprise_groups_edit"
        id="group-options-select"
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        classNamePrefix="group-options"
        className={`w-100 ${noGroupTree ? "" : "edit-mode"} ${menuOpen ? "menu-open" : ""}`}
        components={{ Option: CustomGroupsOption }}
        isMulti
        options={groupsFormData}
        value={groupValues && editing ? Object.values(groupValues).filter(group => !group.unlink) : groupValues}
        isClearable
        maxMenuHeight={300}
        onChange={(selectedOptions, actionMeta) => {
          const formattedOptions = selectedOptions?.map(option => ({
            ...option,
            label: option.label
              ? option.label.replace(/^[\u00A0]+/, "")
              : option.label,
          }));
          handleSelectedOptions(actionMeta, formattedOptions);
        }}

        styles={darkMode ? darkModeSelectStyles : ""}
      />
      }
    </>
  )
}

export default GroupsSelect;
